import { Fragment, useEffect, useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, LinearProgress, Menu, MenuItem, Paper, Snackbar, SnackbarCloseReason, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RepeatIcon from '@mui/icons-material/Repeat';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CancelIcon from '@mui/icons-material/Cancel';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { VTextField, VTextFieldData, VForm, useVForm, IVFormErrors, AutoCompleteParceiro, AutoCompleteBemDoParceiro } from '../../../shared/forms';
import { FerramentasDeDetalhe, TelaBloqueioSalvando } from '../../../shared/components';
import { useBloqueioSalvamento } from '../../../shared/hooks';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { BensService, IBem, IHistoricoBem } from '../../../shared/services/api/servicos/BensService';
import { IDetalheOrdemServico, OrdemServicoService, TOSStatus } from '../../../shared/services/api/servicos/OrdemServicoService';
import { IOrdemServicoHistorico, OrdemServicoHistoricoService } from '../../../shared/services/api/servicos/OrdemServicoHistoricoService';
import { OrdemServicoProdutosService } from '../../../shared/services/api/servicos/OrdemServicoProdutosService';
import { OrdemServicoMaoDeObraService } from '../../../shared/services/api/servicos/OrdemServicoMaoDeObraService';
import { IOrdemServicoFoto, OrdemServicoFotosService } from '../../../shared/services/api/servicos/OrdemServicoFotosService';
import { EmpresaService } from '../../../shared/services/api/cadastros/Empresa';
import { ParceirosService, IDetalheParceiros } from '../../../shared/services/api/cadastros/ParceirosService';
import { Environment } from '../../../shared/environment';
import { DialogCadastrarBem } from './DialogCadastrarBem';
import { DialogItemOS } from './DialogItemOS';
import { DialogMaoDeObraOS } from './DialogMaoDeObraOS';
import { gerarComprovanteOS } from './ComprovanteOS';

// Rótulo amigável de cada status — inclui os que o backend ainda não
// transiciona de verdade (ver comentário de TRANSICOES_PERMITIDAS em
// UpdateStatus.ts/backend), só pra exibição segura caso um dado antigo/
// futuro venha com um desses.
const ROTULO_STATUS: Record<TOSStatus, string> = {
  ABERTA: 'Aberta',
  EM_DIAGNOSTICO: 'Diagnóstico',
  AGUARDANDO_APROVACAO: 'Aguardando Aprovação',
  APROVADA: 'Aprovada',
  EM_EXECUCAO: 'Em Execução',
  AGUARDANDO_PECA: 'Aguardando Peça',
  CONCLUIDA: 'Concluída',
  ENTREGUE: 'Entregue',
  CANCELADA: 'Cancelada',
};

// Etapas do "trilho" visual — só as que o backend de fato transiciona hoje
// (ver TRANSICOES_PERMITIDAS em UpdateStatus.ts). CANCELADA é um desvio, não
// uma etapa linear (pode acontecer a partir de Aberta OU Aguardando Peça),
// então vira um aviso à parte em vez de uma posição no trilho.
const ETAPAS_FLUXO: { status: TOSStatus; rotulo: string }[] = [
  { status: 'ABERTA', rotulo: 'Aberta' },
  { status: 'AGUARDANDO_PECA', rotulo: 'Aguardando Peça' },
  { status: 'CONCLUIDA', rotulo: 'Concluída' },
];

interface IFormData {
  idparceiro: string;
  idbem: string;
  relatocliente: string;
  kmentrada?: number;
  horimetroentrada?: number;
  nivelcombustivel?: string;
  condicaoentrada?: string;
  acessorios?: string;
  observacoesentrada?: string;
  previsaoentrega?: string;
}

const formValidationSchema: yup.ObjectSchema<Pick<IFormData, 'idparceiro' | 'idbem' | 'relatocliente'>> = yup.object().shape({
  idparceiro: yup.string().required('Cliente é obrigatório'),
  idbem: yup.string().required('Selecione o bem (veículo/máquina/patrimônio)'),
  relatocliente: yup.string().required('Relato do cliente é obrigatório').min(3),
});

const formatarMoeda = (valor: number) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const DetalheDeOrdemServico: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { formRef, save } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, progress: saveProgress, iniciar: iniciarSalvamento, concluir: concluirSalvamento, cancelar: cancelarSalvamento } = useBloqueioSalvamento();

  const [idParceiroSelecionado, setIdParceiroSelecionado] = useState('');
  const [bemSelecionado, setBemSelecionado] = useState<IBem | undefined>();
  const [openDialogBem, setOpenDialogBem] = useState(false);

  const [ordemServico, setOrdemServico] = useState<IDetalheOrdemServico>();
  const [clienteDetalhado, setClienteDetalhado] = useState<IDetalheParceiros>();
  const [historico, setHistorico] = useState<IOrdemServicoHistorico[]>([]);
  const [historicoBem, setHistoricoBem] = useState<IHistoricoBem>();
  const [carregandoHistoricoBem, setCarregandoHistoricoBem] = useState(false);

  const [aba, setAba] = useState(0);
  const [openDialogItem, setOpenDialogItem] = useState(false);
  const [openDialogMaoDeObra, setOpenDialogMaoDeObra] = useState(false);
  const [salvandoGestao, setSalvandoGestao] = useState(false);
  const [defeitoConstatado, setDefeitoConstatado] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [salvandoObservacoes, setSalvandoObservacoes] = useState(false);
  const [alterandoStatus, setAlterandoStatus] = useState(false);
  const [confirmarFinalizar, setConfirmarFinalizar] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [gerandoComprovante, setGerandoComprovante] = useState(false);
  const [menuAdicionarAncora, setMenuAdicionarAncora] = useState<HTMLElement | null>(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState<IOrdemServicoFoto>();

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const ehVeiculo = bemSelecionado?.tipo === 'VEICULO' || bemSelecionado?.tipo === 'MOTO';
  const concluida = ordemServico?.status === 'CONCLUIDA';
  const cancelada = ordemServico?.status === 'CANCELADA';
  const travada = concluida || cancelada;

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const carregarOS = () => {
    if (id === 'novo') return;
    setIsLoading(true);
    OrdemServicoService.getById(id)
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOrdemServico(result);
          setDefeitoConstatado(result.defeitoconstatado || '');
          setObservacoes(result.observacoesentrada || '');
        }
      });
    OrdemServicoHistoricoService.getAllByOs(id)
      .then((result) => {
        if (!(result instanceof Error)) setHistorico(result);
      });
  };

  useEffect(() => {
    carregarOS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Histórico do bem (outras OSs + peças já trocadas mais de uma vez) — só
  // depende do idbem, não do id da OS atual (que pode mudar de dono do bem
  // nunca, mas o idbem só fica disponível depois que a OS carrega).
  useEffect(() => {
    if (!ordemServico?.idbem) return;
    setCarregandoHistoricoBem(true);
    BensService.getHistorico(ordemServico.idbem).then((result) => {
      setCarregandoHistoricoBem(false);
      if (!(result instanceof Error)) setHistoricoBem(result);
    });
  }, [ordemServico?.idbem]);

  // Telefone/celular/e-mail do cliente pro card lateral — não vêm no
  // detalhe da OS (só nome/cpf, ver IDetalheOrdemServico), então busca à
  // parte assim que o idparceiro da OS carregada fica disponível.
  useEffect(() => {
    if (!ordemServico?.idparceiro) return;
    ParceirosService.getById(ordemServico.idparceiro).then((result) => {
      if (!(result instanceof Error)) setClienteDetalhado(result);
    });
  }, [ordemServico?.idparceiro]);

  useEffect(() => {
    if (!bemSelecionado?.idbem) return;
    formRef.current?.setFieldValue('idbem', bemSelecionado.idbem);
  }, [bemSelecionado]);

  const handleSelecionarBem = (idbem: string | undefined) => {
    if (!idbem) {
      setBemSelecionado(undefined);
      return;
    }
    BensService.getById(idbem).then((result) => {
      if (!(result instanceof Error)) setBemSelecionado(result);
    });
  };

  const handleSave = (dados: IFormData) => {
    if (!iniciarSalvamento()) return;

    formValidationSchema
      .validate(dados, { abortEarly: false })
      .then(() => {
        setIsLoading(true);
        OrdemServicoService
          .create(dados)
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              cancelarSalvamento();
              setTms('error');
              setMsg(result.message);
              setOpenmsg(true);
            } else {
              setTms('success');
              setMsg(`OS nº ${result.numero} aberta com sucesso!`);
              setOpenmsg(true);
              concluirSalvamento();
              navigate(`/ordensservico/${result.idos}`);
            }
          });
      })
      .catch((errors: yup.ValidationError) => {
        cancelarSalvamento();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleRemoverItem = (iditem: string) => {
    if (!ordemServico) return;
    OrdemServicoProdutosService.deleteById(ordemServico.idos, iditem).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        carregarOS();
      }
    });
  };

  const handleRemoverMaoDeObra = (iditem: string) => {
    if (!ordemServico) return;
    OrdemServicoMaoDeObraService.deleteById(ordemServico.idos, iditem).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        carregarOS();
      }
    });
  };

  const handleUploadFoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = event.target.files?.[0];
    event.target.value = '';
    if (!ordemServico || !arquivo) return;
    setEnviandoFoto(true);
    OrdemServicoFotosService.create(ordemServico.idos, arquivo).then((result) => {
      setEnviandoFoto(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        carregarOS();
      }
    });
  };

  const handleRemoverFoto = (idfoto: string) => {
    if (!ordemServico) return;
    OrdemServicoFotosService.deleteById(ordemServico.idos, idfoto).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        carregarOS();
      }
    });
  };

  const handleSalvarGestao = () => {
    if (!ordemServico) return;
    setSalvandoGestao(true);
    OrdemServicoService.updateById(ordemServico.idos, {
      defeitoconstatado: defeitoConstatado,
    }).then((result) => {
      setSalvandoGestao(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        setTms('success');
        setMsg('Defeito constatado salvo!');
        setOpenmsg(true);
        carregarOS();
      }
    });
  };

  const handleSalvarObservacoes = () => {
    if (!ordemServico) return;
    setSalvandoObservacoes(true);
    OrdemServicoService.updateById(ordemServico.idos, {
      observacoesentrada: observacoes,
    }).then((result) => {
      setSalvandoObservacoes(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        setTms('success');
        setMsg('Observação salva!');
        setOpenmsg(true);
        carregarOS();
      }
    });
  };

  const handleMudarStatus = (novoStatus: TOSStatus) => {
    if (!ordemServico) return;
    setAlterandoStatus(true);
    OrdemServicoService.updateStatus(ordemServico.idos, novoStatus).then((result) => {
      setAlterandoStatus(false);
      setConfirmarFinalizar(false);
      setConfirmarCancelar(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
      } else {
        setTms('success');
        setMsg(`Status alterado para ${ROTULO_STATUS[novoStatus]}.`);
        setOpenmsg(true);
        carregarOS();
      }
    });
  };

  const handleImprimirComprovante = async () => {
    if (!ordemServico) return;
    setGerandoComprovante(true);
    const empresaResult = await EmpresaService.getById();
    const empresa = !(empresaResult instanceof Error) ? empresaResult : undefined;
    try {
      const blob = await gerarComprovanteOS(ordemServico, empresa?.fantasia, empresa?.nome, empresa?.logo);
      const url = URL.createObjectURL(blob);
      window.open(url);
    } finally {
      setGerandoComprovante(false);
    }
  };

  if (id !== 'novo') {
    return (
      <LayoutBaseDePagina
        titulo={ordemServico ? `OS nº ${ordemServico.numero}` : 'Ordem de Serviço'}
        barraDeFerramentas={
          <FerramentasDeDetalhe
            mostrarBotaoSalvar={false}
            mostrarBotaoSalvarEFechar={false}
            mostrarBotaoNovo
            mostrarBotaoApagar={false}
            mostrarBotaoImpressao={concluida}
            mostrarBotaoImpressaoCarregando={gerandoComprovante}
            aoClicarEmImpressao={handleImprimirComprovante}
            aoClicarEmVoltar={() => navigate('/ordensservico')}
            aoClicarEmNovo={() => navigate('/ordensservico/novo')}
          />
        }
      >
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>
        {isLoading && <LinearProgress variant='indeterminate' sx={{ m: 1 }} />}
        {ordemServico && (
          <Grid container spacing={2} sx={{ p: 1 }}>
            {/* Coluna principal */}
            <Grid item xs={12} md={8}>
              <Stack spacing={2}>
                {/* Cabeçalho + trilho de status */}
                <Box component={Paper} variant='outlined' padding={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                    <Box>
                      <Typography variant='h6'>OS nº {ordemServico.numero}</Typography>
                      <Typography variant='body2' color='text.secondary'>Aberta em {dayjs(ordemServico.emissao).format('DD/MM/YYYY HH:mm')}</Typography>
                    </Box>
                    <Chip
                      label={ROTULO_STATUS[ordemServico.status]}
                      color={concluida ? 'success' : cancelada ? 'default' : ordemServico.status === 'AGUARDANDO_PECA' ? 'warning' : 'primary'}
                      variant={cancelada ? 'outlined' : 'filled'}
                    />
                  </Stack>

                  {cancelada ? (
                    <Alert severity='warning' variant='outlined' sx={{ mt: 2 }}>Esta ordem de serviço foi cancelada.</Alert>
                  ) : (
                    <Box sx={{ mt: 3, px: { xs: 0, sm: 2 } }}>
                      <Stack direction='row' alignItems='flex-start'>
                        {ETAPAS_FLUXO.map((etapa, indice) => {
                          const indiceAtual = ETAPAS_FLUXO.findIndex(e => e.status === ordemServico.status);
                          const alcancada = indice <= indiceAtual;
                          return (
                            <Fragment key={etapa.status}>
                              <Stack alignItems='center' spacing={0.5} sx={{ minWidth: 72 }}>
                                {alcancada ? <CheckCircleIcon color='success' fontSize='small' /> : <RadioButtonUncheckedIcon color='disabled' fontSize='small' />}
                                <Typography variant='caption' color={alcancada ? 'text.primary' : 'text.secondary'} align='center'>{etapa.rotulo}</Typography>
                              </Stack>
                              {indice < ETAPAS_FLUXO.length - 1 && (
                                <Box sx={{ flex: 1, height: 2, mt: 1.25, bgcolor: indice < indiceAtual ? 'success.main' : alpha(theme.palette.text.primary, 0.12) }} />
                              )}
                            </Fragment>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Box>

                {/* Card de informações — 3 colunas: Cliente / OS / Equipamento */}
                <Box component={Paper} variant='outlined' padding={2}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Stack direction='row' spacing={1} alignItems='flex-start'>
                        <PersonIcon color='action' />
                        <Box>
                          <Typography variant='subtitle2'>Cliente</Typography>
                          <Typography variant='body2'>{ordemServico.nomeparceiro}</Typography>
                          {ordemServico.cpfparceiro && <Typography variant='caption' color='text.secondary'>{ordemServico.cpfparceiro}</Typography>}
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Stack direction='row' spacing={1} alignItems='flex-start'>
                        <BuildIcon color='action' />
                        <Box>
                          <Typography variant='subtitle2'>Ordem de Serviço</Typography>
                          <Typography variant='body2'>Nº {ordemServico.numero}</Typography>
                          <Typography variant='caption' color='text.secondary'>
                            Previsão: {ordemServico.previsaoentrega ? dayjs(ordemServico.previsaoentrega).format('DD/MM/YYYY') : 'não informada'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Stack direction='row' spacing={1} alignItems='flex-start'>
                        <PrecisionManufacturingIcon color='action' />
                        <Box>
                          <Typography variant='subtitle2'>Equipamento</Typography>
                          <Typography variant='body2'>
                            {ordemServico.descricaobem}
                            {ordemServico.marca || ordemServico.modelo ? ` — ${[ordemServico.marca, ordemServico.modelo].filter(Boolean).join(' ')}` : ''}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {ordemServico.placa ? `Placa ${ordemServico.placa}` : ordemServico.patrimonio ? `Patrimônio ${ordemServico.patrimonio}` : ordemServico.numeroserie ? `S/N ${ordemServico.numeroserie}` : '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  {(ordemServico.relatocliente || ordemServico.condicaoentrada || ordemServico.kmentrada || ordemServico.horimetroentrada) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Grid container spacing={2}>
                        {ordemServico.relatocliente && (
                          <Grid item xs={12}>
                            <Typography variant='caption' color='text.secondary'>Relato do Cliente</Typography>
                            <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>{ordemServico.relatocliente}</Typography>
                          </Grid>
                        )}
                        {(ordemServico.kmentrada || ordemServico.horimetroentrada) && (
                          <Grid item xs={12} sm={4}>
                            <Typography variant='caption' color='text.secondary'>{ordemServico.kmentrada ? 'KM de Entrada' : 'Horímetro de Entrada'}</Typography>
                            <Typography variant='body2'>{ordemServico.kmentrada ?? ordemServico.horimetroentrada}</Typography>
                          </Grid>
                        )}
                        {ordemServico.condicaoentrada && (
                          <Grid item xs={12} sm={8}>
                            <Typography variant='caption' color='text.secondary'>Condição de Entrada</Typography>
                            <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>{ordemServico.condicaoentrada}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </Box>

                {/* Tabs: Serviços/Itens | Observações | Histórico | Fotos */}
                <Box component={Paper} variant='outlined'>
                  <Tabs value={aba} onChange={(_, novaAba) => setAba(novaAba)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Tab label='Serviços / Itens' />
                    <Tab label='Observações' />
                    <Tab label='Histórico' />
                    <Tab label={`Fotos${ordemServico.fotos.length > 0 ? ` (${ordemServico.fotos.length})` : ''}`} />
                  </Tabs>

                  {aba === 0 && (
                    <Box padding={2} display='flex' flexDirection='column' gap={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            size='small'
                            label='Defeito Constatado'
                            placeholder='O que o técnico encontrou ao diagnosticar'
                            value={defeitoConstatado}
                            disabled={travada || salvandoGestao}
                            onChange={(e) => setDefeitoConstatado(e.target.value)}
                          />
                        </Grid>
                        {!travada && (
                          <Grid item xs={12}>
                            <Button variant='outlined' disabled={salvandoGestao} onClick={handleSalvarGestao}>
                              Salvar Defeito Constatado
                            </Button>
                          </Grid>
                        )}
                      </Grid>

                      <Divider />

                      <Box>
                        <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
                          <Typography variant='subtitle2'>Mão de Obra</Typography>
                          {!travada && (
                            <IconButton size='small' color='primary' onClick={() => setOpenDialogMaoDeObra(true)}>
                              <AddIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>Mecânico</TableCell>
                              <TableCell>Serviço Realizado</TableCell>
                              <TableCell align='right'>Valor</TableCell>
                              {!travada && <TableCell align='center'>Ações</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ordemServico.maodeobra.map((item) => (
                              <TableRow key={item.iditem}>
                                <TableCell>{item.mecanico}</TableCell>
                                <TableCell>{item.descricao}</TableCell>
                                <TableCell align='right'>{formatarMoeda(item.valor)}</TableCell>
                                {!travada && (
                                  <TableCell align='center'>
                                    <IconButton size='small' color='error' onClick={() => handleRemoverMaoDeObra(item.iditem)}>
                                      <DeleteIcon fontSize='small' />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                            {ordemServico.maodeobra.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={travada ? 3 : 4}>Nenhuma mão de obra adicionada.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>

                      <Divider />

                      <Box>
                        <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
                          <Typography variant='subtitle2'>Peças e Produtos</Typography>
                          {!travada && (
                            <IconButton size='small' color='primary' onClick={() => setOpenDialogItem(true)}>
                              <AddIcon />
                            </IconButton>
                          )}
                        </Box>
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>Descrição</TableCell>
                              <TableCell>Un</TableCell>
                              <TableCell align='right'>Qtd</TableCell>
                              <TableCell align='right'>Valor Unit.</TableCell>
                              <TableCell align='right'>Desconto</TableCell>
                              <TableCell align='right'>Total</TableCell>
                              {!travada && <TableCell align='center'>Ações</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ordemServico.itens.map((item) => (
                              <TableRow key={item.iditem}>
                                <TableCell>{item.descricao}{item.avulso ? ' (avulso)' : ''}</TableCell>
                                <TableCell>{item.unidade || '-'}</TableCell>
                                <TableCell align='right'>{item.quantidade}</TableCell>
                                <TableCell align='right'>{formatarMoeda(item.valorunitario)}</TableCell>
                                <TableCell align='right'>{formatarMoeda(item.desconto)}</TableCell>
                                <TableCell align='right'>{formatarMoeda(item.total)}</TableCell>
                                {!travada && (
                                  <TableCell align='center'>
                                    <IconButton size='small' color='error' onClick={() => handleRemoverItem(item.iditem)}>
                                      <DeleteIcon fontSize='small' />
                                    </IconButton>
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                            {ordemServico.itens.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={travada ? 6 : 7}>Nenhuma peça/produto adicionado.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>

                      <Divider />
                      <Grid container spacing={1} justifyContent='flex-end'>
                        <Grid item xs={12} sm={5}>
                          <Typography align='right' variant='body2' color='text.secondary'>Peças: {formatarMoeda(ordemServico.vprodutos)}</Typography>
                          <Typography align='right' variant='body2' color='text.secondary'>Mão de obra: {formatarMoeda(ordemServico.vservicos)}</Typography>
                          <Typography align='right' variant='h6'>Total do Serviço: {formatarMoeda(ordemServico.total)}</Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {aba === 1 && (
                    <Box padding={2} display='flex' flexDirection='column' gap={2}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label='Observações'
                        placeholder='Anotações internas sobre o atendimento'
                        value={observacoes}
                        disabled={travada || salvandoObservacoes}
                        onChange={(e) => setObservacoes(e.target.value)}
                      />
                      {!travada && (
                        <Box>
                          <Button variant='outlined' disabled={salvandoObservacoes} onClick={handleSalvarObservacoes}>
                            Salvar Observação
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                  {aba === 2 && (
                    <Box padding={2} display='flex' flexDirection='column' gap={3}>
                      <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Linha do tempo desta OS</Typography>
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Evento</TableCell>
                              <TableCell>Descrição</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {historico.map(item => (
                              <TableRow key={item.idhistorico}>
                                <TableCell>{dayjs(item.criadoem).format('DD/MM/YYYY HH:mm')}</TableCell>
                                <TableCell>{item.tipo}</TableCell>
                                <TableCell>{item.descricao}</TableCell>
                              </TableRow>
                            ))}
                            {historico.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={3}>Nenhum evento registrado.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>

                      <Divider />

                      {carregandoHistoricoBem && <LinearProgress variant='indeterminate' />}

                      <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Outras ordens de serviço deste bem</Typography>
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell>Nº</TableCell>
                              <TableCell>Emissão</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align='right'>Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(historicoBem?.ordens || []).map((item) => (
                              <TableRow key={item.idos} selected={item.idos === ordemServico.idos}>
                                <TableCell>{item.numero}{item.idos === ordemServico.idos ? ' (esta OS)' : ''}</TableCell>
                                <TableCell>{dayjs(item.emissao).format('DD/MM/YYYY')}</TableCell>
                                <TableCell>{item.status}</TableCell>
                                <TableCell align='right'>{formatarMoeda(item.total)}</TableCell>
                              </TableRow>
                            ))}
                            {(!historicoBem || historicoBem.ordens.length === 0) && !carregandoHistoricoBem && (
                              <TableRow>
                                <TableCell colSpan={4}>Nenhuma outra OS encontrada para este bem.</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>

                      <Box>
                        <Typography variant='subtitle2' sx={{ mb: 1 }}>Peças trocadas mais de uma vez neste bem</Typography>
                        {historicoBem && historicoBem.pecasRepetidas.length === 0 && !carregandoHistoricoBem && (
                          <Alert severity='success' variant='outlined'>Nenhuma peça foi trocada mais de uma vez neste bem até agora.</Alert>
                        )}
                        {(historicoBem?.pecasRepetidas || []).map((peca) => (
                          <Box key={peca.chave} component={Paper} variant='outlined' padding={1.5} sx={{ mb: 1.5 }}>
                            <Box display='flex' alignItems='center' gap={1} sx={{ mb: 1 }}>
                              <RepeatIcon color='warning' fontSize='small' />
                              <Typography variant='subtitle2'>{peca.descricao}</Typography>
                              <Chip size='small' color='warning' variant='outlined' label={`${peca.ocorrencias.length}x trocada`} />
                            </Box>
                            <Table size='small'>
                              <TableHead>
                                <TableRow>
                                  <TableCell>OS</TableCell>
                                  <TableCell>Data</TableCell>
                                  <TableCell align='right'>Qtd</TableCell>
                                  <TableCell align='right'>Dias desde a troca anterior</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {peca.ocorrencias.map((ocorrencia, indice) => (
                                  <TableRow key={ocorrencia.idos}>
                                    <TableCell>{ocorrencia.numero}</TableCell>
                                    <TableCell>{dayjs(ocorrencia.emissao).format('DD/MM/YYYY')}</TableCell>
                                    <TableCell align='right'>{ocorrencia.quantidade}</TableCell>
                                    <TableCell align='right'>{indice === 0 ? '—' : `${peca.diasEntreTrocas[indice - 1]} dias`}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {aba === 3 && (
                    <Box padding={2} display='flex' flexDirection='column' gap={2}>
                      <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1}>
                        <Typography variant='body2' color='text.secondary'>
                          {ordemServico.fotos.length}/10 fotos — use pra registrar a condição do bem ou comprovar um defeito.
                        </Typography>
                        {!travada && ordemServico.fotos.length < 10 && (
                          <Button component='label' variant='outlined' size='small' startIcon={<AddIcon />} disabled={enviandoFoto}>
                            Adicionar Foto
                            <input type='file' accept='image/png,image/jpeg,image/jpg,image/gif,image/webp' hidden onChange={handleUploadFoto} />
                          </Button>
                        )}
                      </Box>
                      {enviandoFoto && <LinearProgress variant='indeterminate' />}
                      <Grid container spacing={2}>
                        {ordemServico.fotos.map((foto) => (
                          <Grid item xs={6} sm={4} md={3} key={foto.idfoto}>
                            <Box
                              component={Paper}
                              variant='outlined'
                              sx={{ position: 'relative', aspectRatio: '1 / 1', overflow: 'hidden', cursor: 'pointer' }}
                              onClick={() => setFotoAmpliada(foto)}
                            >
                              <Box
                                component='img'
                                src={`${Environment.URL_OSFOTOS}${foto.arquivo}`}
                                alt={foto.descricao || 'Foto da ordem de serviço'}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                              {!travada && (
                                <IconButton
                                  size='small'
                                  onClick={(e) => { e.stopPropagation(); handleRemoverFoto(foto.idfoto); }}
                                  sx={{ position: 'absolute', top: 4, right: 4, bgcolor: alpha(theme.palette.background.paper, 0.85), '&:hover': { bgcolor: theme.palette.background.paper } }}
                                >
                                  <DeleteIcon fontSize='small' color='error' />
                                </IconButton>
                              )}
                            </Box>
                          </Grid>
                        ))}
                        {ordemServico.fotos.length === 0 && (
                          <Grid item xs={12}>
                            <Alert severity='info' variant='outlined'>Nenhuma foto adicionada ainda.</Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Grid>

            {/* Coluna lateral — Cliente / Equipamento / Ações Rápidas */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Box component={Paper} variant='outlined' padding={2}>
                  <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main' }}>
                      {(ordemServico.nomeparceiro || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant='subtitle1'>{ordemServico.nomeparceiro}</Typography>
                      <Chip size='small' label='Cliente Ativo' color='success' variant='outlined' />
                    </Box>
                  </Stack>
                  <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                    {(clienteDetalhado?.celular || clienteDetalhado?.telefone) && (
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <PhoneIcon fontSize='small' color='action' />
                        <Typography variant='body2'>{clienteDetalhado?.celular || clienteDetalhado?.telefone}</Typography>
                      </Stack>
                    )}
                    {clienteDetalhado?.email && (
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <EmailIcon fontSize='small' color='action' />
                        <Typography variant='body2'>{clienteDetalhado.email}</Typography>
                      </Stack>
                    )}
                    {(clienteDetalhado?.endereco || clienteDetalhado?.bairro) && (
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <PlaceIcon fontSize='small' color='action' />
                        <Typography variant='body2'>{[clienteDetalhado?.endereco, clienteDetalhado?.bairro].filter(Boolean).join(', ')}</Typography>
                      </Stack>
                    )}
                  </Stack>
                  <Button fullWidth size='small' variant='outlined' endIcon={<ChevronRightIcon />} onClick={() => navigate(`/parceiros/${ordemServico.idparceiro}`)}>
                    Ver histórico do cliente
                  </Button>
                </Box>

                <Box component={Paper} variant='outlined' padding={2}>
                  <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 1.5 }}>
                    <Inventory2Icon color='action' />
                    <Typography variant='subtitle1'>Equipamento</Typography>
                  </Stack>
                  <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    <Typography variant='body2'>
                      {ordemServico.descricaobem}
                      {ordemServico.marca || ordemServico.modelo ? ` — ${[ordemServico.marca, ordemServico.modelo].filter(Boolean).join(' ')}` : ''}
                    </Typography>
                    {ordemServico.numeroserie && <Typography variant='caption' color='text.secondary' display='block'>S/N: {ordemServico.numeroserie}</Typography>}
                    {ordemServico.placa && <Typography variant='caption' color='text.secondary' display='block'>Placa: {ordemServico.placa}</Typography>}
                    {ordemServico.patrimonio && <Typography variant='caption' color='text.secondary' display='block'>Patrimônio: {ordemServico.patrimonio}</Typography>}
                  </Stack>
                  <Button fullWidth size='small' variant='outlined' endIcon={<ChevronRightIcon />} onClick={() => navigate(`/bens/${ordemServico.idbem}`)}>
                    Ver detalhes do equipamento
                  </Button>
                </Box>

                <Box component={Paper} variant='outlined' padding={2}>
                  <Typography variant='subtitle1' sx={{ mb: 1.5 }}>Ações Rápidas</Typography>
                  <Stack spacing={1}>
                    {!travada && (
                      <Button fullWidth variant='contained' color='success' disabled={alterandoStatus} onClick={() => setConfirmarFinalizar(true)}>
                        Finalizar OS
                      </Button>
                    )}
                    {!travada && (
                      <Button fullWidth variant='outlined' onClick={(e) => setMenuAdicionarAncora(e.currentTarget)}>
                        Adicionar Serviço / Peça
                      </Button>
                    )}
                    <Menu anchorEl={menuAdicionarAncora} open={!!menuAdicionarAncora} onClose={() => setMenuAdicionarAncora(null)}>
                      <MenuItem onClick={() => { setMenuAdicionarAncora(null); setOpenDialogItem(true); }}>Peça / Produto</MenuItem>
                      <MenuItem onClick={() => { setMenuAdicionarAncora(null); setOpenDialogMaoDeObra(true); }}>Mão de Obra</MenuItem>
                    </Menu>
                    <Button fullWidth variant='outlined' startIcon={<EditNoteIcon />} onClick={() => setAba(1)}>
                      Registrar Observação
                    </Button>
                    <Button fullWidth variant='outlined' startIcon={<PhotoCameraIcon />} onClick={() => setAba(3)}>
                      {ordemServico.fotos.length > 0 ? `Ver Fotos (${ordemServico.fotos.length}/10)` : 'Adicionar Foto'}
                    </Button>
                    {!travada && ordemServico.status !== 'AGUARDANDO_PECA' && (
                      <Button fullWidth variant='text' disabled={alterandoStatus} onClick={() => handleMudarStatus('AGUARDANDO_PECA')}>
                        Marcar Aguardando Peça
                      </Button>
                    )}
                    {!travada && ordemServico.status === 'AGUARDANDO_PECA' && (
                      <Button fullWidth variant='text' disabled={alterandoStatus} onClick={() => handleMudarStatus('ABERTA')}>
                        Reabrir (voltar de Aguardando Peça)
                      </Button>
                    )}
                    {!travada && (
                      <Button fullWidth variant='outlined' color='error' startIcon={<CancelIcon />} onClick={() => setConfirmarCancelar(true)}>
                        Cancelar OS
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        )}

        {ordemServico && (
          <DialogItemOS
            open={openDialogItem}
            idos={ordemServico.idos}
            onClose={() => { setOpenDialogItem(false); carregarOS(); }}
            aoSalvar={() => carregarOS()}
          />
        )}

        {ordemServico && (
          <DialogMaoDeObraOS
            open={openDialogMaoDeObra}
            idos={ordemServico.idos}
            onClose={() => { setOpenDialogMaoDeObra(false); carregarOS(); }}
            aoSalvar={() => carregarOS()}
          />
        )}

        <Dialog open={confirmarFinalizar} onClose={() => setConfirmarFinalizar(false)}>
          <DialogTitle>Finalizar Ordem de Serviço?</DialogTitle>
          <DialogContent>
            <Typography>
              Depois de finalizada, a OS não pode mais ser alterada (itens, mão de obra, defeito e status ficam travados).
              Ela vai aparecer em Financeiro &gt; Faturamento &gt; Ordens de Serviço pra decidir a forma de pagamento.
              Total: {ordemServico ? formatarMoeda(ordemServico.total) : '-'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmarFinalizar(false)} disabled={alterandoStatus}>Cancelar</Button>
            <Button variant='contained' color='success' disabled={alterandoStatus} onClick={() => handleMudarStatus('CONCLUIDA')}>
              Finalizar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={confirmarCancelar} onClose={() => setConfirmarCancelar(false)}>
          <DialogTitle>Cancelar Ordem de Serviço?</DialogTitle>
          <DialogContent>
            <Typography>
              A OS será marcada como cancelada, os itens dela deixam de reservar estoque e ela não poderá mais ser alterada.
              Essa ação não pode ser desfeita.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmarCancelar(false)} disabled={alterandoStatus}>Voltar</Button>
            <Button variant='contained' color='error' disabled={alterandoStatus} onClick={() => handleMudarStatus('CANCELADA')}>
              Cancelar OS
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!fotoAmpliada} onClose={() => setFotoAmpliada(undefined)} maxWidth='md'>
          {fotoAmpliada && (
            <Box
              component='img'
              src={`${Environment.URL_OSFOTOS}${fotoAmpliada.arquivo}`}
              alt={fotoAmpliada.descricao || 'Foto da ordem de serviço'}
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block' }}
            />
          )}
        </Dialog>
      </LayoutBaseDePagina>
    );
  }

  return (
    <>
      <TelaBloqueioSalvando
        open={isSaving}
        message='Abrindo ordem de serviço...'
        progress={saveProgress}
      />
      <LayoutBaseDePagina
        titulo='Nova Ordem de Serviço'
        barraDeFerramentas={
          <FerramentasDeDetalhe
            mostrarBotaoSalvarEFechar={false}
            mostrarBotaoSalvarCarregando={isLoading}
            mostrarBotaoNovo={false}
            mostrarBotaoApagar={false}
            mostrarBotaoImpressao={false}
            disableBotaoSalvar={isLoading || isSaving}
            aoClicarEmSalvar={save}
            aoClicarEmVoltar={() => navigate('/ordensservico')}
          />
        }
      >
        <VForm ref={formRef} onSubmit={handleSave} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
            <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
              {msg}
            </Alert>
          </Snackbar>
          <Stack spacing={2} sx={{ p: 1 }}>
            {isLoading && <LinearProgress variant='indeterminate' />}

            <Box component={Paper} variant='outlined' padding={2}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <PersonIcon color='action' />
                <Typography variant='subtitle1'>Cliente e Equipamento</Typography>
              </Stack>
              <Grid container spacing={2} alignItems='flex-start'>
                <Grid item xs={12} sm={12} md={6}>
                  <AutoCompleteParceiro isExternalLoading={isLoading} uuidparceiro={(id) => setIdParceiroSelecionado(id ?? '')} />
                </Grid>
                <Grid item xs={10} sm={10} md={5}>
                  <AutoCompleteBemDoParceiro isExternalLoading={isLoading} idparceiro={idParceiroSelecionado} aoSelecionar={handleSelecionarBem} />
                </Grid>
                <Grid item xs={2} sm={2} md={1}>
                  <Tooltip title={idParceiroSelecionado ? 'Cadastrar novo bem' : 'Selecione um cliente primeiro'}>
                    <span>
                      <IconButton
                        color='primary'
                        disabled={!idParceiroSelecionado}
                        onClick={() => setOpenDialogBem(true)}
                      >
                        <AddIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>

                {bemSelecionado && (
                  <Grid item xs={12}>
                    <Alert severity='info' variant='outlined'>
                      {bemSelecionado.descricao}
                      {bemSelecionado.marca || bemSelecionado.modelo ? ` — ${[bemSelecionado.marca, bemSelecionado.modelo].filter(Boolean).join(' ')}` : ''}
                      {bemSelecionado.placa ? ` — Placa ${bemSelecionado.placa}` : ''}
                      {bemSelecionado.patrimonio ? ` — Patrimônio ${bemSelecionado.patrimonio}` : ''}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box component={Paper} variant='outlined' padding={2}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <EditNoteIcon color='action' />
                <Typography variant='subtitle1'>Relato do Cliente</Typography>
              </Stack>
              <VTextField size='small' fullWidth multiline rows={3} name='relatocliente' disabled={isLoading} label='Relato do Cliente' placeholder='O que o cliente informou sobre o problema' />
            </Box>

            <Box component={Paper} variant='outlined' padding={2}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <BuildIcon color='action' />
                <Typography variant='subtitle1'>Detalhes de Entrada</Typography>
              </Stack>
              <Grid container spacing={2}>
                {ehVeiculo ? (
                  <>
                    <Grid item xs={6} sm={3}>
                      <VTextField size='small' fullWidth name='kmentrada' disabled={isLoading} label='KM de Entrada' />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <VTextField size='small' fullWidth name='nivelcombustivel' disabled={isLoading} label='Combustível' />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={6} sm={3}>
                    <VTextField size='small' fullWidth name='horimetroentrada' disabled={isLoading} label='Horímetro de Entrada' />
                  </Grid>
                )}
                <Grid item xs={6} sm={3}>
                  <VTextFieldData fullWidth name='previsaoentrega' disabled={isLoading} label='Previsão de Entrega' />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <VTextField size='small' fullWidth multiline rows={2} name='condicaoentrada' disabled={isLoading} label='Condição de Entrada' />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <VTextField size='small' fullWidth multiline rows={2} name='acessorios' disabled={isLoading} label='Acessórios Entregues' />
                </Grid>
              </Grid>
            </Box>

            <Box component={Paper} variant='outlined' padding={2}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <EditNoteIcon color='action' />
                <Typography variant='subtitle1'>Observações</Typography>
              </Stack>
              <VTextField size='small' fullWidth multiline rows={2} name='observacoesentrada' disabled={isLoading} label='Observações' />
            </Box>
          </Stack>
        </VForm>
      </LayoutBaseDePagina>
      <DialogCadastrarBem
        open={openDialogBem}
        idparceiro={idParceiroSelecionado}
        onClose={() => setOpenDialogBem(false)}
        aoCriar={(idbem) => handleSelecionarBem(idbem)}
      />
    </>
  );
};
